import { brandApi } from "../lib/axios";

interface ShopifyShop {
  name: string;
  email: string;
  myshopifyDomain: string;
  primaryDomain: {
    host: string;
    url: string;
  };
  billingAddress: {
    name: string | null;
    address1: string | null;
    city: string | null;
    province: string | null;
    country: string;
    zip: string | null;
  };
}

type OnboardingStatus = "PERSONAL_DETAILS" | "COMPANY_DETAIL" | "COMPLETED";

interface OnboardingStatusData {
  id: string;
  profileImage: string;
  firstName: string;
  lastName: string;
  email: string;
  country: string;
  password: string;
  companyName: string;
  website: string;
  description: string;
  sessionId: string;
  brandBannerImage: string;
  brandProfileImage: string;
  createdAt: string;
  updatedAt: string;
  onboardingStatus: OnboardingStatus;
}

interface ShopifyDetailResponse {
  shop: ShopifyShop;
  onboardingStatus?: {
    status: OnboardingStatus;
    data: OnboardingStatusData;
  };
}

interface PersonalDetails {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  country: string;
  profileImage?: string;
}

interface CompanyDetails {
  companyName: string;
  website: string;
  niches: string[];
  description: string;
  brandProfileImage?: string;
  brandBannerImage?: string;
  landingFreeTrial?: boolean;
}

export interface BrandAuthResponse {
  user: {
    id: string;
    name: string;
    email: string;
    shop: string;
    role: "BRAND";
  };
  token: string;
}

interface SignInCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
}

interface SignInResponse {
  access_token: string;
  refresh_token: string;
  brand: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    shop: string;
    profileImage: string;
    role: "BRAND";
  };
}

export const brandAuthService = {
  async getShopifyDetail(token: string) {
    const response = await brandApi.get<ShopifyDetailResponse>(
      "/brand/shopify-detail",
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data;
  },

  async registerPersonalDetails(data: PersonalDetails, token: string) {
    // Payout methods are no longer collected at signup; the brand is asked
    // for them when the first affiliate payout is ready to be paid.
    const response = await brandApi.post(
      "/brand/personal-detail",
      data,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data;
  },

  async registerCompanyDetails(data: CompanyDetails, token: string) {
    const response = await brandApi.post("/brand/company-detail", data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async verifyToken(token: string) {
    const response = await brandApi.get<BrandAuthResponse>(
      `/brand/verify?token=${token}`
    );
    return response.data;
  },

  async getAuthStatus(shop: string) {
    const response = await brandApi.get<BrandAuthResponse>(
      `/brand/auth-status?shop=${shop}`
    );
    return response.data;
  },

  signIn: async (credentials: SignInCredentials): Promise<SignInResponse> => {
    const response = await brandApi.post<SignInResponse>(
      "/brand/signin",
      credentials
    );

    // Save tokens after successful login
    localStorage.setItem("brand_token", response.data.access_token);

    return response.data;
  },
};
